import Form from '@components/Form'
import { useCreateFieldBoundary } from '@features/fields/api/useCreateFieldBoundary'
import { BoundarySaveOutcome, type ClientDispatchedToolResult } from 'api-validations/aurora'
import {
  deleteBoundaryApi,
  getAllBoundariesApi,
  getPlanningCffsApi,
  PlanAssetCreateError,
  type PlanAssetCreateErrorResult
} from 'api-validations/locutus'
import { useEffect, useState } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaMutation, usePanoramaQuery } from 'shared-ui/api-provider'
import { useAuroraIncomingHandlers } from 'shared-ui/aurora'
import { FieldBoundaryIcon, InnerAreaIcon } from 'shared-ui/components'
import { useLocalization } from 'shared-ui/localization'
import { useMapContext } from 'shared-ui/map'
import { Geo, isNonNil } from 'utils'
import { sameCff } from 'utils/cff'

import { BottomButtons } from './BoundaryOptions'
import { ConfirmAdditionalBoundaryModal } from './ConfirmAdditionalBoundaryModal'
import { AreaToken, ToolsAndShapes } from './ReusedComponents'

type SaveBoundaryResult = ClientDispatchedToolResult<'SaveBoundary'>

export const BoundaryDrawing = ({
  fieldId,
  returnToMain,
  boundaryName,
  defaultName
}: {
  fieldId: string
  returnToMain: (boundary?: { id: string; name: string }) => void
  boundaryName?: string
  defaultName?: string
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const [spatialStats, setSpatialStats] = useState(() => {
    const { covered, holes, holeCount } = Geo.getAreas(map.drawTool.getFeatureCollection())
    return { covered, holes, holeCount }
  })

  useEffect(() => {
    const { cleanup } = map.drawTool.onChange((collection) => {
      const area = Geo.getAreas(collection)
      setSpatialStats(area)
    })

    return cleanup
  }, [map.drawTool])

  const orgCode = useOrgCodeOrThrow()
  const save = useCreateFieldBoundary((boundary) => {
    map.drawTool.disable()
    returnToMain(boundary)
    map.drawTool.clear()
  })

  const existingBoundaries = usePanoramaQuery(
    getAllBoundariesApi,
    { orgCode },
    { select: ({ items }) => items.filter((b) => b.fieldId === fieldId) }
  )
  const openField = usePanoramaQuery(
    getPlanningCffsApi,
    { orgCode },
    { select: ({ items }) => items.find((cff) => cff.id === fieldId) ?? null }
  )
  const deleteBoundary = usePanoramaMutation(deleteBoundaryApi)
  const [showConfirm, setShowConfirm] = useState(false)

  const [name, setName] = useState(
    isNonNil(boundaryName) ? `${boundaryName} - ${t(($) => $.labels.copy)}` : (defaultName ?? '')
  )

  const createBoundary = () => save.mutate([{ name, fieldId }])

  const saveErrorText = (error: PlanAssetCreateErrorResult) =>
    error.type === PlanAssetCreateError.DuplicateName
      ? t(($) => $.planning.duplicateAssetName.Boundary)
      : error.type === PlanAssetCreateError.TooLarge
        ? t(($) => $.planning.assetTooLargeToSave)
        : error.message || t(($) => $.messages.errorOccurredTitle)

  useAuroraIncomingHandlers({
    dispatch: {
      FillBoundaryInnerAreas: () => {
        const { holeCount } = Geo.getAreas(map.drawTool.getFeatureCollection())
        map.drawTool.clearInnerRings()
        const { covered } = Geo.getAreas(map.drawTool.getFeatureCollection())
        return { filledCount: holeCount, outlinedAcres: covered }
      },
      SaveBoundary: async ({ client, farm, field, name: requestedName }) => {
        if (spatialStats.covered === 0) return { outcome: BoundarySaveOutcome.NothingToSave }
        if (openField.data === undefined || existingBoundaries.data === undefined) {
          return {
            outcome: BoundarySaveOutcome.Failed,
            reason: "The field's details are still loading; try again in a moment."
          }
        }
        if (openField.data === null || !sameCff(openField.data, { client, farm, field })) {
          return {
            outcome: BoundarySaveOutcome.Failed,
            reason: `The editor is not open on ${field}, so nothing was saved; open its outline first.`
          }
        }
        if (existingBoundaries.data.length > 0) {
          return { outcome: BoundarySaveOutcome.HasExistingBoundary }
        }

        const savedName = requestedName ?? name
        if (savedName.length === 0) {
          return {
            outcome: BoundarySaveOutcome.Failed,
            reason: 'The outline has no name yet; ask the user what to call it.'
          }
        }
        setName(savedName)

        const { covered, holes, holeCount } = spatialStats
        return save
          .mutateAsync([{ name: savedName, fieldId }])
          .then((): SaveBoundaryResult => ({
            outcome: BoundarySaveOutcome.Saved,
            name: savedName,
            acres: covered,
            innerAreas: { count: holeCount, acres: holes }
          }))
          .catch((error: PlanAssetCreateErrorResult): SaveBoundaryResult => ({
            outcome: BoundarySaveOutcome.Failed,
            reason: saveErrorText(error)
          }))
      }
    }
  })

  const requestSave = () => {
    if ((existingBoundaries.data?.length ?? 0) > 0) setShowConfirm(true)
    else createBoundary()
  }

  const replaceExisting = async () => {
    try {
      await Promise.all(
        (existingBoundaries.data ?? []).map((b) =>
          deleteBoundary.mutateAsync({ params: { orgCode, boundaryId: b.id } })
        )
      )
    } catch {
      // Surfaced via deleteBoundary.isError in the modal; keep it open so the
      // user can retry rather than silently dropping the failure.
      return
    }
    setShowConfirm(false)
    createBoundary()
  }

  return (
    <>
      <div className="flex w-full flex-col gap-2 border-b border-gray-300 pb-4">
        <h5 className="pan-title4">{t(($) => $.titles.boundaryDetails)}</h5>
        <Form.Label htmlFor="boundary-name">{t(($) => $.labels.boundaryName)}</Form.Label>
        <Form.Input
          id="boundary-name"
          value={name}
          onChange={({ target }) => setName(target.value)}
          placeholder={t(($) => $.labels.name)}
          className="mb-0 w-full"
        />
        <div className="grid grid-cols-2 gap-3">
          <AreaToken
            Icon={FieldBoundaryIcon}
            type={t(($) => $.agConcepts.field, { count: 1 })}
            area={spatialStats.covered}
          />
          <AreaToken
            reset={true}
            onReset={map.drawTool.clearInnerRings}
            Icon={InnerAreaIcon}
            type={t(($) => $.planning.boundaryCreation.inner)}
            area={spatialStats.holes}
          />
        </div>
      </div>
      <ToolsAndShapes type="boundary" />

      <BottomButtons
        onCancel={() => {
          map.drawTool.disable()
          map.drawTool.clear()
          returnToMain()
        }}
        onSave={requestSave}
        saveDisabledWhen={{
          when: spatialStats.covered === 0 || name.length === 0,
          reason: t(($) => $.planning.boundaryCreation.drawBoundaryAndNameFirst)
        }}
        savePending={save.isPending}
        saveError={save.isError ? saveErrorText(save.error) : undefined}
      />
      <ConfirmAdditionalBoundaryModal
        show={showConfirm}
        existingCount={existingBoundaries.data?.length ?? 0}
        onCreateAnother={() => {
          setShowConfirm(false)
          createBoundary()
        }}
        onReplace={replaceExisting}
        onCancel={() => setShowConfirm(false)}
        pending={deleteBoundary.isPending || save.isPending}
        error={
          deleteBoundary.isError
            ? t(($) => $.planning.boundaryCreation.confirmAdditional.replaceFailed)
            : undefined
        }
      />
    </>
  )
}
